import { useState, useEffect } from 'react';
import { CheckSquare, Phone, Mail, Video, FileText, ArrowUpDown, Plus, Check, Trash2, Clock, AlertCircle } from 'lucide-react';

export default function SalesTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: 'pending' });

  useEffect(() => { loadTasks(); }, [filter]);

  async function loadTasks() {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.status) params.set('status', filter.status);
    if (filter.taskType) params.set('taskType', filter.taskType);
    try {
      const r = await fetch(`/api/crm/tasks?${params}`, { credentials: 'include' });
      const d = await r.json();
      setTasks(d.tasks || []);
    } catch {} finally { setLoading(false); }
  }

  async function completeTask(id) {
    await fetch(`/api/crm/tasks/${id}/complete`, { method: 'PATCH', credentials: 'include' });
    loadTasks();
  }

  async function deleteTask(id) {
    await fetch(`/api/crm/tasks/${id}`, { method: 'DELETE', credentials: 'include' });
    loadTasks();
  }

  const now = new Date();
  const overdue = tasks.filter(t => t.status === 'pending' && t.due_date && new Date(t.due_date) < now);
  const dueToday = tasks.filter(t => t.status === 'pending' && t.due_date && new Date(t.due_date).toDateString() === now.toDateString());
  const upcoming = tasks.filter(t => t.status === 'pending' && (!t.due_date || new Date(t.due_date) > now) && !dueToday.includes(t));
  const completed = tasks.filter(t => t.status === 'completed');

  const typeIcons = { call: Phone, email: Mail, meeting: Video, todo: CheckSquare, follow_up: ArrowUpDown };

  function TaskCard({ task }) {
    const Icon = typeIcons[task.task_type] || FileText;
    const isOverdue = task.status === 'pending' && task.due_date && new Date(task.due_date) < now;
    return (
      <div className={`bg-white rounded-lg border p-3 flex items-start gap-3 ${isOverdue ? 'border-red-200' : 'border-gray-200'}`}>
        <button onClick={() => task.status === 'pending' && completeTask(task.id)}
          className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${task.status === 'completed' ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-400'}`}>
          {task.status === 'completed' && <Check size={12} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={14} className="text-gray-400 flex-shrink-0" />
            <span className={`text-sm font-medium ${task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{task.title}</span>
          </div>
          {task.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{task.description}</p>}
          <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-400">
            {task.contact_name?.trim() && <span>{task.contact_name}</span>}
            {task.deal_title && <span>• {task.deal_title}</span>}
            {task.due_date && <span className={isOverdue ? 'text-red-500 font-medium' : ''}>
              <Clock size={10} className="inline mr-0.5" />
              {new Date(task.due_date).toLocaleDateString()}
            </span>}
          </div>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
          task.priority === 'high' ? 'bg-red-100 text-red-700' : task.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
        }`}>{task.priority}</span>
        {task.status === 'pending' && (
          <button onClick={() => deleteTask(task.id)} className="text-gray-300 hover:text-red-500 flex-shrink-0"><Trash2 size={14} /></button>
        )}
      </div>
    );
  }

  function Section({ title, items, icon: SIcon, color }) {
    if (items.length === 0) return null;
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <SIcon size={14} className={color} />
          <h3 className="text-sm font-semibold text-gray-700">{title} ({items.length})</h3>
        </div>
        <div className="space-y-1.5">{items.map(t => <TaskCard key={t.id} task={t} />)}</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
      </div>
      <div className="flex gap-2 mb-4">
        {['pending', 'completed'].map(s => (
          <button key={s} onClick={() => setFilter({ ...filter, status: s })}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter.status === s ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`}>
            {s === 'pending' ? 'Active' : 'Completed'}
          </button>
        ))}
      </div>
      {loading ? <div className="flex justify-center py-12"><div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-500" /></div> : (
        <>
          <Section title="Overdue" items={overdue} icon={AlertCircle} color="text-red-500" />
          <Section title="Due Today" items={dueToday} icon={Clock} color="text-amber-500" />
          <Section title="Upcoming" items={upcoming} icon={CheckSquare} color="text-blue-500" />
          {filter.status === 'completed' && <Section title="Completed" items={completed} icon={Check} color="text-green-500" />}
          {tasks.length === 0 && <div className="p-12 text-center text-gray-400 text-sm">No tasks yet</div>}
        </>
      )}
    </div>
  );
}
